import React from 'react';
import {
  Feedback,
  fieldFeedbackContainer,
  requiredLabel,
} from '@noths/polaris-client-ribbons-design-system';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import { ToggleButton } from 'src/components/organisms/Personalisation/components/ToggleButton';
import * as copy from 'src/components/organisms/Personalisation/constants/copy';
import * as styles from 'src/components/organisms/Personalisation/styles/Personalisation.styles';
import {
  getFieldPosition,
  getProgressMilestone,
} from 'src/components/organisms/Personalisation/utils';
import type {
  InteractionErrorTrackingEvent,
  InteractionTrackingEvent,
  ProgressTrackingEvent,
  SelectionPayload,
  TrackedEvents,
} from 'src/redux/basket/types';
import type {
  CatServiceAPIProductOption,
  CatServiceAPIProductOptionValue,
} from 'src/services/cat-service-api/types/CatServiceAPIProductOption';

const getId = (id: number) => `${id}`;

export interface OwnProps extends CatServiceAPIProductOption {
  displayValidation: boolean | undefined;
}

export interface DispatchProps {
  deselectOption: (selection: SelectionPayload) => void;
  selectOption: (selection: SelectionPayload) => void;
  trackInteraction: (payload: InteractionTrackingEvent) => void;
  trackInteractionError: (payload: InteractionErrorTrackingEvent) => void;
  trackProgressMilestone: (payload: ProgressTrackingEvent) => void;
}

export interface ContainerProps {
  currency: CurrencyCode;
  error: boolean;
  options: CatServiceAPIProductOption[];
  selectedOption?: CatServiceAPIProductOptionValue[];
  totalOutstandingSelections: number;
  totalRequiredOptions: number;
  trackedEvents: TrackedEvents;
}

type Props = ContainerProps & DispatchProps & OwnProps;

export const MultipleOptionSelection = ({
  currency,
  deselectOption,
  error,
  options,
  selectOption,
  totalOutstandingSelections,
  totalRequiredOptions,
  trackInteraction,
  trackProgressMilestone,
  trackedEvents,
  ...props
}: Props) => {
  const appliedValue = props.selectedOption?.[0];
  const isValidSelection = !!appliedValue && !error;
  const errorContainerId = `selection-error-${props.id}`;

  const handleOnChange = (
    newValue: CatServiceAPIProductOptionValue | null,
    currentValueName: string,
  ) => {
    const fieldName = props.name;
    const fieldPosition = getFieldPosition(options, props);

    trackInteraction({
      field_position: fieldPosition,
      field_name: fieldName,
      field_action: 'Field Input',
      field_type: '3 and more values selection',
      field_value: newValue ? currentValueName : null,
      required_field: props.required,
    });

    const milestone = getProgressMilestone({
      totalRequiredOptions,
      totalOutstandingSelections,
      trackedEvents,
      fieldName,
      isRequiredOption: props.required,
    });

    if (milestone) {
      trackProgressMilestone({
        field_position: fieldPosition,
        field_name: fieldName,
        field_action: milestone,
        field_type: '3 and more values selection',
        field_value: currentValueName,
        required_field: true,
      });
    }

    if (newValue) {
      selectOption({
        optionId: props.id,
        optionValue: newValue,
        type: 'single-selection',
      });
    } else if (appliedValue) {
      deselectOption({
        optionId: props.id,
        optionValue: appliedValue,
        type: 'single-selection',
      });
    }
  };

  const handleFocusTracking = (valueName: string) => {
    if (trackedEvents.focus[props.name]) {
      return;
    }

    trackInteraction({
      field_position: getFieldPosition(options, props),
      field_name: props.name,
      field_action: 'Field Focus',
      field_type: '3 and more values selection',
      field_value: valueName,
      required_field: props.required,
    });
  };

  return (
    <div
      aria-describedby={errorContainerId}
      aria-labelledby={getId(props.id)}
      css={[styles.radioGroup, styles.selectionContainer]}
      data-has-error={error}
      data-is-valid={isValidSelection}
      role="group"
    >
      {props.required && (
        <p
          aria-hidden="true"
          css={requiredLabel}
          data-has-error={error}
          data-is-valid={isValidSelection}
        >
          {copy.REQUIRED_COPY}
        </p>
      )}
      <p
        css={styles.groupLabel}
        dangerouslySetInnerHTML={{ __html: props.name }}
        id={getId(props.id)}
      />
      <p css={styles.toggleButtonGroupDescription}>{copy.TOGGLE_BUTTON_GROUP_DESCRIPTION}</p>
      {props.values?.map((value) => (
        <ToggleButton
          currency={currency}
          isSelected={value.id === appliedValue?.id}
          key={value.id}
          onClick={(newValue) => handleOnChange(newValue, value.name)}
          onFocus={() => handleFocusTracking(value.name)}
          value={value}
        />
      ))}
      <div css={[fieldFeedbackContainer, styles.feedbackContainer]} id={errorContainerId}>
        {error && <Feedback error iconAlign="left" name={copy.REQUIRED_SELECTION_COPY} value="" />}
      </div>
    </div>
  );
};
